import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, 
  Calendar, 
  Users, 
  Briefcase, 
  UserCog, 
  Settings, 
  BarChart3,
  Bell,
  CreditCard,
  Star,
  Clock,
  Globe,
  Menu,
  X,
  LogOut,
  ChevronDown,
  IdCard,
  DoorOpen,
  PartyPopper,
  FileText,
  ClipboardList,
  Shield,
  TrendingUp,
  DollarSign,
  BookOpen,
  ShoppingBag,
  Gauge,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useLocationContext } from "@/contexts/LocationContext";
import { useBusiness } from "@/hooks/use-business";
import { useNotifications } from "@/hooks/use-notifications";
import { usePlatformAdmin } from "@/hooks/use-platform-admin";
import { useTrial } from "@/hooks/use-trial";
import { useCountdown } from "@/hooks/use-countdown";
import { AlertCircle, MapPin } from "lucide-react";
import { isNativeIosApp, isNativeApp } from "@/lib/platform";
import { isPerBookingBilling, needsPerBookingSignupFee, isPerBookingSignupLocked } from "@/lib/billing-model";
import { openBuukdSupportWhatsApp } from "@/lib/support";

interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
  badgeCount?: number;
}

const secondaryNavItemsBase: NavItem[] = [
  { label: "Usage", href: "/app/usage", icon: Gauge },
  { label: "Billing", href: "/app/billing", icon: CreditCard },
  { label: "Settings", href: "/app/settings", icon: Settings },
];

interface AppShellProps {
  children: React.ReactNode;
  businessName?: string;
}

export function AppShell({ children, businessName = "My Business" }: AppShellProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { business } = useBusiness();
  const { counts, markTypeRead } = useNotifications();
  const { isSuperAdmin } = usePlatformAdmin();
  const { isTrial, daysRemaining, isLocked, isInGrace, trialEndsAt, graceEndsAt } = useTrial();
  const isNativeIos = isNativeIosApp();
  const isNativeMobile = isNativeApp();
  
  // Location context - only use if available (for app routes)
  let locationContext: ReturnType<typeof useLocationContext> | null = null;
  try {
    locationContext = useLocationContext();
  } catch {
    // Not in LocationProvider context (public routes)
  }
  
  // Check if business is in grace period
  const isGracePeriod = (business as any)?.subscription_status === "grace_period";
  const perBookingAccount = isPerBookingBilling(business);
  const perBookingActivationGrace =
    perBookingAccount && needsPerBookingSignupFee(business) && !isPerBookingSignupLocked(business);
  const showTrialUi = (isTrial && !perBookingAccount) || perBookingActivationGrace;
  const gracePeriodEndsAt = (business as any)?.subscription_grace_period_ends_at;
  const countdown = useCountdown(gracePeriodEndsAt);

  const userName = user?.user_metadata?.name || user?.email?.split('@')[0] || "User";
  const userInitial = userName.charAt(0).toUpperCase();

  const enabledModules = useMemo(() => {
    // Primary module is always enabled. Extra modules are stored in business_modules.
    // For now we only use primary module for nav gating (keeps things non-breaking).
    const primary = (business as any)?.primary_module || "appointments";
    return new Set<string>([primary]);
  }, [business]);

  const mainNavItems = useMemo<NavItem[]>(() => {
    const primary = (business as any)?.primary_module || "appointments";
    const bookingUnread = Number(counts["booking_created"] || 0);
    // Orders = food order (cart) submissions only; Catering requests = event/buffet form submissions only
    const ordersUnread = Number(counts["food_order_submission_created"] || 0);
    const cateringRequestUnread = Number(counts["catering_request_created"] || 0);

    if (primary === "catering_services" || primary === "food_vendors") {
      const eventCateringEnabled = (business as any)?.event_catering_requests_enabled === true;
      return [
        { label: "Overview", href: "/app", icon: LayoutDashboard },
        { label: "Customers", href: "/app/customers", icon: Users },
        { label: "Menu Items", href: "/app/menu-items/manage", icon: BookOpen },
        { label: "Orders", href: "/app/orders", icon: ClipboardList, badgeCount: ordersUnread },
        ...(eventCateringEnabled
          ? [{ label: "Catering requests", href: "/app/catering-requests", icon: PartyPopper, badgeCount: cateringRequestUnread }]
          : []),
        { label: "Delivery Team", href: "/app/staff", icon: UserCog },
        { label: "Orders Page", href: "/app/orders-page", icon: Globe },
        { label: "Marketing", href: "/app/marketing", icon: Bell },
        { label: "Invoicing", href: "/app/invoicing", icon: FileText },
        { label: "Analytics", href: "/app/analytics", icon: BarChart3 },
      ];
    }

    if (primary === "memberships") {
      const accessDeskEnabled = (business as any)?.access_desk_enabled !== false; // default true
      return [
        { label: "Overview", href: "/app", icon: LayoutDashboard },
        { label: "Memberships", href: "/app/memberships", icon: IdCard },
        ...(accessDeskEnabled ? [{ label: "Access Desk", href: "/app/access-desk", icon: DoorOpen }] : []),
        { label: "Customers", href: "/app/customers", icon: Users },
        { label: "Marketing", href: "/app/marketing", icon: Bell },
        { label: "Events", href: "/app/events", icon: PartyPopper },
        { label: "Invoicing", href: "/app/invoicing", icon: FileText },
        { label: "Analytics", href: "/app/analytics", icon: BarChart3 },
      ];
    }

    if (primary === "clinics") {
      const appointmentsInvoicingEnabled = (business as any)?.appointments_invoicing_enabled === true;
      const logBookEnabled = (business as any)?.log_book_enabled === true;
      const salesEnabled = (business as any)?.sales_enabled === true;

      return [
        { label: "Overview", href: "/app", icon: LayoutDashboard },
        { label: "Appointments", href: "/app/appointments", icon: Calendar, badgeCount: bookingUnread },
        { label: "Services", href: "/app/services", icon: Briefcase },
        ...(salesEnabled ? [{ label: "Sales", href: "/app/sales", icon: ShoppingBag }] : []),
        { label: "Staff", href: "/app/staff", icon: UserCog },
        { label: "Visit Notes", href: "/app/visit-notes", icon: FileText },
        ...(appointmentsInvoicingEnabled ? [{ label: "Invoicing", href: "/app/invoicing", icon: FileText }] : []),
        ...(logBookEnabled ? [{ label: "Log Book", href: "/app/log-book", icon: BookOpen }] : []),
        { label: "Patients", href: "/app/customers", icon: Users },
        { label: "Marketing", href: "/app/marketing", icon: Bell },
        { label: "Analytics", href: "/app/analytics", icon: BarChart3 },
      ];
    }

    // appointments (default)
    const appointmentsInvoicingEnabled = (business as any)?.appointments_invoicing_enabled === true;
    const logBookEnabled = (business as any)?.log_book_enabled === true;
    const salesEnabled = (business as any)?.sales_enabled === true;

    return [
      { label: "Overview", href: "/app", icon: LayoutDashboard },
      { label: "Appointments", href: "/app/appointments", icon: Calendar, badgeCount: bookingUnread },
      { label: "Events", href: "/app/events", icon: PartyPopper },
      { label: "Services", href: "/app/services", icon: Briefcase },
      ...(salesEnabled ? [{ label: "Sales", href: "/app/sales", icon: ShoppingBag }] : []),
      ...(appointmentsInvoicingEnabled ? [{ label: "Invoicing", href: "/app/invoicing", icon: FileText }] : []),
      ...(logBookEnabled ? [{ label: "Log Book", href: "/app/log-book", icon: BookOpen }] : []),
      { label: "Staff", href: "/app/staff", icon: UserCog },
      { label: "Customers", href: "/app/customers", icon: Users },
      { label: "Marketing", href: "/app/marketing", icon: Bell },
      { label: "Analytics", href: "/app/analytics", icon: BarChart3 },
    ];
  }, [business, counts]);

  // Shared inbox: mark notifications read when related pages are opened
  useEffect(() => {
    if (location.pathname === "/app/appointments" && Number(counts["booking_created"] || 0) > 0) {
      markTypeRead("booking_created");
    }
    if (location.pathname === "/app/orders" && Number(counts["food_order_submission_created"] || 0) > 0) {
      markTypeRead("food_order_submission_created");
    }
    if (location.pathname === "/app/catering-requests" && Number(counts["catering_request_created"] || 0) > 0) {
      markTypeRead("catering_request_created");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, counts]);

  const secondaryNavItems = useMemo<NavItem[]>(() => {
    const primary = (business as any)?.primary_module || "appointments";
    const superAdminItems: NavItem[] = isSuperAdmin ? [
      { label: "Super Admin", href: "/app/super-admin", icon: Shield },
      { label: "Analytics", href: "/app/super-admin/analytics", icon: TrendingUp },
      { label: "Payments", href: "/app/super-admin/payments", icon: DollarSign },
      { label: "Settlements", href: "/app/platform/payouts", icon: CreditCard },
    ] : [];
    const secondaryBase = isNativeIos
      ? secondaryNavItemsBase.filter((item) => item.href !== "/app/billing")
      : secondaryNavItemsBase;

    if (primary === "appointments" || primary === "clinics" || enabledModules.has("appointments")) {
      const base = [...secondaryBase, { label: "Booking Page", href: "/app/booking-page", icon: Globe }];
      return [...base, ...superAdminItems];
    }
    return [...secondaryBase, ...superAdminItems];
  }, [business, enabledModules, isNativeIos, isSuperAdmin]);

  const handleSignOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast({
        title: "Error signing out",
        description: error.message,
        variant: "destructive",
      });
    } else {
      navigate("/signin");
    }
  };

  const handleSubscriptionAction = () => {
    if (isNativeMobile) {
      openBuukdSupportWhatsApp();
      return;
    }

    navigate("/app/billing");
  };

  const NavLink = ({ item }: { item: NavItem }) => {
    const isActive = location.pathname === item.href;
    return (
      <Link
        to={item.href}
        className={cn(
          "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors shrink-0",
          isActive
            ? "bg-accent text-accent-foreground"
            : "text-muted-foreground hover:bg-accent/50 hover:text-foreground"
        )}
        onClick={() => setSidebarOpen(false)}
      >
        <item.icon className="h-5 w-5 shrink-0" />
        <span className="flex-1 min-w-0">{item.label}</span>
        {item.badgeCount && item.badgeCount > 0 ? (
          <span className="inline-flex min-w-[18px] h-[18px] px-1 items-center justify-center rounded-full bg-primary text-primary-foreground text-[11px] font-semibold shrink-0">
            {item.badgeCount > 99 ? "99+" : item.badgeCount}
          </span>
        ) : null}
      </Link>
    );
  };

  const accentTheme = ((business as any)?.accent_theme === "emerald" ? "lavender" : (business as any)?.accent_theme) || "blue";

  // Apply accent theme to body so portaled content (dialogs, dropdowns) inherits --primary/--ring
  useEffect(() => {
    const body = document.body;
    const themeClasses = ["app-theme-pink", "app-theme-purple", "app-theme-lavender", "app-theme-teal", "app-theme-navy", "app-theme-charcoal"];
    themeClasses.forEach((c) => body.classList.remove(c));
    if (accentTheme !== "blue") {
      body.classList.add(`app-theme-${accentTheme}`);
    }
    return () => themeClasses.forEach((c) => body.classList.remove(c));
  }, [accentTheme]);

  return (
    <div
      className={cn(
        "min-h-screen bg-subtle-gray",
        accentTheme !== "blue" && `app-theme-${accentTheme}`
      )}
    >
      {/* Mobile header — ios-safe-top adds env(safe-area-inset-top) for notch/Dynamic Island */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-background border-b border-border ios-safe-top">
        <div className="h-16 flex items-center px-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(true)}
            className="relative overflow-visible rounded-full bg-primary/10 text-primary hover:bg-primary/15"
            aria-label="Open main navigation"
          >
            <span className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
            <span className="absolute inset-0 rounded-full shadow-[0_0_20px_hsl(var(--primary)/0.35)] animate-pulse" />
            <Menu className="relative h-5 w-5" />
          </Button>
          <div className="ml-4 flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
              <Calendar className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="font-semibold text-foreground">{businessName}</span>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 bg-background border-r border-border transform transition-transform lg:translate-x-0 flex flex-col",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {/* Logo */}
        <div className="flex-shrink-0 flex items-center justify-between px-4 border-b border-border ios-safe-top" style={{ minHeight: "4rem" }}>
          <Link to="/app" className="flex items-center gap-2">
            <span className="text-xl font-bold">
              <span className="text-primary">Buukd</span>
            </span>
          </Link>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Business selector with location dropdown */}
        <div className="flex-shrink-0 p-4 border-b border-border">
          {locationContext && locationContext.locations.length > 1 ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-full flex items-start gap-3 px-3 py-2 bg-accent rounded-lg hover:bg-accent/80 transition-colors text-left">
                  <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Calendar className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-foreground leading-snug line-clamp-2 break-words">
                      {businessName}
                    </div>
                    {locationContext.selectedLocationId ? (
                      <div className="text-xs text-muted-foreground truncate flex items-center gap-1">
                        <MapPin className="h-3 w-3 flex-shrink-0" />
                        {locationContext.locations.find(loc => loc.id === locationContext.selectedLocationId)?.name || "All Locations"}
                      </div>
                    ) : (
                      <div className="text-xs text-muted-foreground truncate flex items-center gap-1">
                        <MapPin className="h-3 w-3 flex-shrink-0" />
                        All Locations
                      </div>
                    )}
                  </div>
                  <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-1" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">Business</div>
                <div className="px-2 py-1.5 text-sm text-foreground">{businessName}</div>
                <DropdownMenuSeparator />
                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">Filter by Location</div>
                <DropdownMenuItem
                  onClick={() => locationContext?.setSelectedLocationId(null)}
                  className={!locationContext?.selectedLocationId ? "bg-accent" : ""}
                >
                  <MapPin className="mr-2 h-4 w-4" />
                  All Locations
                </DropdownMenuItem>
                {locationContext.locations.map((loc) => (
                  <DropdownMenuItem
                    key={loc.id}
                    onClick={() => locationContext?.setSelectedLocationId(loc.id)}
                    className={locationContext?.selectedLocationId === loc.id ? "bg-accent" : ""}
                  >
                    <MapPin className="mr-2 h-4 w-4" />
                    {loc.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex items-center gap-3 px-3 py-2 bg-accent rounded-lg">
              <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Calendar className="h-4 w-4 text-primary" />
              </div>
              <span className="font-medium text-sm text-foreground leading-snug line-clamp-2 break-words">
                {businessName}
              </span>
            </div>
          )}
        </div>

        {/* Navigation - scrollable */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-1 min-h-0">
          <div className="space-y-1">
            {mainNavItems.map((item) => (
              <div key={`main-${item.href}`} className="shrink-0">
                <NavLink item={item} />
              </div>
            ))}
          </div>
          
          <div className="pt-4 mt-4 border-t border-border space-y-1">
            {secondaryNavItems.map((item) => (
              <div key={`secondary-${item.href}`} className="shrink-0">
                <NavLink item={item} />
              </div>
            ))}
          </div>
        </nav>

        {/* Bottom section */}
        <div className="flex-shrink-0 p-4 border-t border-border">
          {showTrialUi && (
            <div className="mb-4 p-3 bg-accent rounded-lg">
              <p className="text-sm font-medium text-primary">
                {isNativeIos ? "Account Access" : "Free Trial"}
              </p>
              <p className="text-xs text-muted-foreground">
                {isNativeIos
                  ? "Contact support if you need help"
                  : `${daysRemaining} ${daysRemaining === 1 ? "day" : "days"} remaining`}
              </p>
            </div>
          )}
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-accent transition-colors">
                <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-sm font-medium">
                  {userInitial}
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-foreground leading-snug break-words">
                    {userName}
                  </p>
                </div>
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuItem asChild>
                <Link to="/app/settings">Settings</Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-foreground/20 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Header safe-area offset (mobile fixed header) */}
      <div className="lg:pl-64 min-h-screen mobile-header-safe-area">
        {/* Grace Period Banner - Full width, positioned before main content */}
        {isGracePeriod && !perBookingAccount && gracePeriodEndsAt && (
          <div className="bg-red-600 text-white border-b border-red-700">
            <div className="px-6 lg:px-8 py-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-start gap-3 flex-1">
                  <AlertCircle className="h-5 w-5 text-white flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-base mb-1">
                      {isNativeIos ? "Account access needs attention" : "Payment Failed - Account Will Be Suspended"}
                    </h3>
                    <p className="text-sm text-red-50 mb-2">
                      {isNativeIos
                        ? "Your account needs attention. Please contact Buukd support to avoid account suspension."
                        : "Your subscription payment failed. Please update your payment method to avoid account suspension."}
                    </p>
                    {isNativeIos ? null : !countdown.isExpired ? (
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <span className="text-red-100">Time remaining:</span>
                        <span className="bg-red-700 px-2 py-1 rounded font-mono">
                          {countdown.days > 0 && `${countdown.days}d `}
                          {String(countdown.hours).padStart(2, "0")}:
                          {String(countdown.minutes).padStart(2, "0")}:
                          {String(countdown.seconds).padStart(2, "0")}
                        </span>
                      </div>
                    ) : (
                      <div className="text-sm font-medium text-red-100">
                        Grace period expired. Account suspended.
                      </div>
                    )}
                  </div>
                </div>
                <Button
                  onClick={handleSubscriptionAction}
                  className="bg-white text-red-600 hover:bg-red-50 font-semibold whitespace-nowrap"
                  size="sm"
                >
                  {isNativeIos ? "Contact support" : "Resubscribe Now"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Trial Banner */}
        {showTrialUi && !isGracePeriod && (
          <TrialBanner
            isLocked={isLocked}
            isInGrace={isInGrace}
            daysRemaining={daysRemaining}
            trialEndsAt={trialEndsAt}
            graceEndsAt={graceEndsAt}
            onUpgrade={handleSubscriptionAction}
            isNativeMobile={isNativeMobile}
            activationGrace={perBookingActivationGrace}
          />
        )}

        {/* Main content — pb-safe adds env(safe-area-inset-bottom) for iOS home indicator */}
        <main className="lg:pt-0 overflow-x-hidden">
          <div className="p-6 lg:p-8 pb-safe">{children}</div>
        </main>
      </div>
    </div>
  );
}

function TrialBanner(props: {
  isLocked: boolean;
  isInGrace: boolean;
  daysRemaining: number;
  trialEndsAt: string | null;
  graceEndsAt: string | null;
  onUpgrade: () => void;
  isNativeMobile: boolean;
  activationGrace?: boolean;
}) {
  // During grace, count down to lock (grace end). During active trial, count down to trial end.
  const countdownTarget = props.isInGrace ? props.graceEndsAt : props.trialEndsAt;
  const countdown = useCountdown(countdownTarget);
  const showCountdown = !props.isLocked && !countdown.isExpired && countdown.totalSeconds > 0;

  if (props.isNativeMobile) {
    return (
      <div className="border-b border-red-200 bg-red-50 text-red-900">
        <div className="px-4 sm:px-6 lg:px-8 py-2.5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
            <div className="flex items-start gap-2.5 flex-1 min-w-0">
              <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5 text-red-600" />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm leading-snug break-words">
                  Account access needs setup
                </h3>
                <p className="text-xs mt-0.5 text-red-800/80">
                  Contact Buukd support if you need help with access.
                </p>
              </div>
            </div>
            <Button
              onClick={props.onUpgrade}
              size="sm"
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-100 font-semibold whitespace-nowrap w-full sm:w-auto sm:self-center"
            >
              Contact support
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border-b border-red-200 bg-red-50 text-red-900">
      <div className="px-4 sm:px-6 lg:px-8 py-2.5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
          <div className="flex items-start gap-2.5 flex-1 min-w-0">
            <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5 text-red-600" />
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm leading-snug break-words">
                {props.activationGrace
                  ? props.daysRemaining <= 0
                    ? "Pay your activation fee today to keep access"
                    : `Pay your activation fee in the next ${props.daysRemaining} ${props.daysRemaining === 1 ? "day" : "days"}`
                  : props.isLocked
                    ? "Your free trial has ended. Upgrade to continue."
                    : props.isInGrace
                      ? props.daysRemaining <= 0
                        ? "Your grace period ends today. Upgrade to keep access."
                        : `Your grace period ends in ${props.daysRemaining} ${props.daysRemaining === 1 ? "day" : "days"}`
                      : props.daysRemaining <= 0
                        ? "Your free trial ends today"
                        : `Your free trial ends in ${props.daysRemaining} ${props.daysRemaining === 1 ? "day" : "days"}`}
              </h3>
              {showCountdown ? (
                <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-medium text-red-800/80">
                  <span>{props.activationGrace ? "Account locks in" : "Locks in"}</span>
                  <span className="bg-white/70 border border-red-200 px-2 py-0.5 rounded font-mono whitespace-nowrap text-red-900">
                    {countdown.days > 0 && `${countdown.days}d `}
                    {String(countdown.hours).padStart(2, "0")}:
                    {String(countdown.minutes).padStart(2, "0")}:
                    {String(countdown.seconds).padStart(2, "0")}
                  </span>
                  {props.activationGrace ? (
                    <span>
                      Pay on Billing to stay active. No monthly fee after that.
                    </span>
                  ) : (
                    <span>Upgrade or pick a plan so your account is not locked.</span>
                  )}
                </div>
              ) : (
                <p className="text-xs mt-0.5 text-red-800/80">
                  {props.activationGrace
                    ? "Pay your activation fee on Billing before time runs out. No monthly subscription on this plan."
                    : props.isLocked
                      ? "Your account is locked. Upgrade to get back in."
                      : "Upgrade or pick a plan so your account is not locked."}
                </p>
              )}
            </div>
          </div>
          <Button
            onClick={props.onUpgrade}
            size="sm"
            variant="outline"
            className="border-red-300 text-red-700 hover:bg-red-100 font-semibold whitespace-nowrap w-full sm:w-auto sm:self-center"
          >
            {props.activationGrace ? "Go to Billing" : "Upgrade"}
          </Button>
        </div>
      </div>
    </div>
  );
}
